import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { ImportField, ParsedCsv } from './importCsv';
import { contactsMessages as messages } from './contactsMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Outreach area; each part uses its own slice of it */
import styles from './Contacts.module.scss';
/* eslint-enable css-modules/no-unused-class */

const FIELDS: Array<{ field: ImportField; label: MessageDescriptor }> = [
  { field: 'name', label: messages.fieldName },
  { field: 'email', label: messages.fieldEmail },
  { field: 'kind', label: messages.fieldKind },
  { field: 'url', label: messages.fieldUrl },
  { field: 'tags', label: messages.fieldTags },
  { field: 'skip', label: messages.fieldSkip },
];

interface ImportColumnsStepProps {
  fileName: string;
  csv: ParsedCsv;
  mapping: ImportField[];
  onChange: (mapping: ImportField[]) => void;
}

/** Step 2 of the import (design outreach q3): what each column holds. Email is required. */
const ImportColumnsStepComponent: React.FC<ImportColumnsStepProps> = ({ fileName, csv, mapping, onChange }) => {
  const intl = useIntl();
  const options = useMemo(
    () =>
      FIELDS.map((item) => (
        <option key={item.field} value={item.field}>
          {intl.formatMessage(item.label)}
        </option>
      )),
    [intl]
  );
  const rows = useMemo(
    () =>
      csv.headers.map((header, index) => (
        // eslint-disable-next-line react/no-array-index-key -- two columns can have the same header
        <div key={`${header}-${index}`} className={styles.mapRow}>
          <span className="fw-semibold">{header}</span>
          <span aria-hidden="true">→</span>
          <select
            className="form-select form-select-sm"
            aria-label={header}
            value={mapping[index] ?? 'skip'}
            onChange={(event) => {
              const next = [...mapping];
              const field = event.target.value as ImportField;
              // One field one column: the old column of this field skips.
              const old = next.indexOf(field);
              if (field !== 'skip' && old >= 0) {
                next[old] = 'skip';
              }
              next[index] = field;
              onChange(next);
            }}
          >
            {options}
          </select>
          <span className={styles.mapSample}>{csv.rows[0]?.[index] ?? ''}</span>
        </div>
      )),
    [csv, mapping, onChange, options]
  );

  return (
    <>
      <p className="mb-0">{intl.formatMessage(messages.columnsBody, { file: fileName, count: csv.rows.length })}</p>
      <div className={styles.mapRow}>
        <span className="small text-body-secondary">{intl.formatMessage(messages.colYours)}</span>
        <span />
        <span className="small text-body-secondary">{withBrandWord(intl.formatMessage(messages.colField))}</span>
        <span className="small text-body-secondary">{intl.formatMessage(messages.colSample)}</span>
      </div>
      {rows}
      {!mapping.includes('email') && <p className="text-danger mb-0">{intl.formatMessage(messages.noEmailColumn)}</p>}
    </>
  );
};

export const ImportColumnsStep = TypedMemo(ImportColumnsStepComponent);
